import React from 'react';
import { Row, Col, Card, Button, Spinner, Badge } from 'react-bootstrap';
import { TrendingUp, Home, DollarSign, Clock, Users, Eye } from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface MarketData {
  activeListings: number;
  avgDaysOnMarket: number;
  medianPrice: number;
  myListings: number;
  clientMatches: number;
  recentActivity: any[];
  priceHistory: any[];
  marketTrends: any[];
}

interface AgentMarketOverviewProps {
  data?: MarketData;
  isLoading: boolean;
}

const AgentMarketOverview: React.FC<AgentMarketOverviewProps> = ({
  data,
  isLoading
}) => {
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading market overview...</p>
      </div>
    );
  }

  const priceChartData = {
    labels: data?.priceHistory?.map(item => item.month) || [],
    datasets: [{
      label: 'Median Price',
      data: data?.priceHistory?.map(item => item.price) || [],
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.4
    }]
  };

  const trendsChartData = {
    labels: ['New Listings', 'Pending', 'Sold', 'Expired'],
    datasets: [{
      data: data?.marketTrends || [45, 32, 28, 12],
      backgroundColor: ['#10b981', '#f59e0b', '#3b82f6', '#ef4444']
    }]
  };

  return (
    <div className="agent-market-overview">
      {/* Key Metrics Cards */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="avatar-sm bg-soft-primary rounded me-3">
                  <Home className="avatar-xs text-primary" />
                </div>
                <div>
                  <h4 className="mb-1 fw-bold">{data?.activeListings || 0}</h4>
                  <p className="text-muted mb-0 font-13">Active Listings</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="avatar-sm bg-soft-success rounded me-3">
                  <Clock className="avatar-xs text-success" />
                </div>
                <div>
                  <h4 className="mb-1 fw-bold">{data?.avgDaysOnMarket || 0}</h4>
                  <p className="text-muted mb-0 font-13">Avg Days on Market</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="avatar-sm bg-soft-info rounded me-3">
                  <DollarSign className="avatar-xs text-info" />
                </div>
                <div>
                  <h4 className="mb-1 fw-bold">${(data?.medianPrice || 0).toLocaleString()}</h4>
                  <p className="text-muted mb-0 font-13">Median Price</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="avatar-sm bg-soft-warning rounded me-3">
                  <Users className="avatar-xs text-warning" />
                </div>
                <div>
                  <h4 className="mb-1 fw-bold">{data?.clientMatches || 0}</h4>
                  <p className="text-muted mb-0 font-13">Client Matches</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Price Trends Chart */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="border-bottom-0 pb-0">
              <h6 className="mb-0">
                <TrendingUp size={18} className="me-2" />
                Market Price Trends
              </h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Line data={priceChartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: false,
                      ticks: {
                        callback: (value) => `$${(value as number).toLocaleString()}`
                      }
                    }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Quick Actions */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="border-bottom-0 pb-0">
              <h6 className="mb-0">Quick Actions</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => window.location.href = '/mls-portal?tab=listings&action=add'}
                >
                  <Home size={16} className="me-2" />
                  Add New Listing
                </Button>
                
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => window.location.href = '/mls-portal?tab=clients'}
                >
                  <Users size={16} className="me-2" />
                  Match Clients
                </Button>
                
                <Button 
                  variant="outline-success" 
                  size="sm"
                  onClick={() => window.location.href = '/mls-portal?tab=analytics'}
                >
                  <TrendingUp size={16} className="me-2" />
                  View Analytics
                </Button>
                
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={() => window.location.href = '/mls-portal?tab=search'}
                >
                  <Eye size={16} className="me-2" />
                  Browse Market
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Market Distribution */}
          <Card className="border-0 shadow-sm mt-3">
            <Card.Header className="border-bottom-0 pb-0">
              <h6 className="mb-0">Market Distribution</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '200px' }}>
                <Bar data={trendsChartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false }
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Client Matches */}
      <Row className="mt-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="border-bottom-0 pb-0">
              <h6 className="mb-0">Recent Client Matches</h6>
            </Card.Header>
            <Card.Body>
              {data?.recentActivity?.length ? (
                <div className="table-responsive">
                  <table className="table table-borderless">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Property Type</th>
                        <th>Location</th>
                        <th>Match Score</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentActivity.map((match, index) => (
                        <tr key={index}>
                          <td>
                            <div>
                              <strong>{match.clientName}</strong>
                              <small className="text-muted d-block">{match.clientEmail}</small>
                            </div>
                          </td>
                          <td>{match.propertyType}</td>
                          <td>{match.location}</td>
                          <td>
                            <Badge bg="success" className="rounded-pill">
                              {match.matchScore}% match
                            </Badge>
                          </td>
                          <td>
                            <Badge 
                              bg={match.status === 'interested' ? 'warning' : 'info'}
                              className="rounded-pill"
                            >
                              {match.status}
                            </Badge>
                          </td>
                          <td>
                            <Button variant="outline-primary" size="sm">
                              Contact
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Users size={48} className="text-muted mb-3" />
                  <p className="text-muted">No recent matches found</p>
                  <Button variant="primary" size="sm">
                    Run Client Matching
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AgentMarketOverview;